// A member's user tag (PRIVACY_PLAN.md §15): the handle a friend in another
// club types to find and follow them, since a stranger only ever sees
// "First L.". Mirrors the API's UserTagRules and the app's user_tag.dart;
// change all three together. The API is the judge of uniqueness; these rules
// only save a round trip and word the prompt.

export const TAG_MIN = 3;
export const TAG_MAX = 20;

const SHAPE = /^[a-z0-9][a-z0-9._]*[a-z0-9]$/;
const RESERVED = new Set([
  'admin', 'administrator', 'support', 'help', 'tapleagues', 'tap_leagues', 'tap.leagues',
  'staff', 'official', 'system', 'root', 'moderator', 'mod', 'null', 'undefined', 'anonymous',
]);

/** Trimmed, lower case, leading @ dropped; '' when blank. */
export function normaliseTag(raw) {
  let tag = String(raw ?? '').trim();
  if (tag.startsWith('@')) tag = tag.slice(1).trim();
  return tag.toLowerCase();
}

/** Why a user tag is not allowed, or '' when its shape is fine. */
export function tagProblem(tag) {
  if (!tag) return 'Choose a user tag.';
  if (tag.length < TAG_MIN) return `User tags are at least ${TAG_MIN} characters.`;
  if (tag.length > TAG_MAX) return `User tags are at most ${TAG_MAX} characters.`;
  if (!SHAPE.test(tag)) return 'Use lower case letters, numbers, dots and underscores, starting and ending with a letter or number.';
  if (/\.\.|__|\._|_\./.test(tag)) return 'Dots and underscores cannot sit next to each other.';
  if (RESERVED.has(tag)) return 'That user tag is reserved.';
  return '';
}

/** What a user tag field says under itself while the person types. */
export function tagHint({tag, checking, available, reason, current}) {
  if (!tag) return 'Lower case letters, numbers, dots and underscores. Friends type this to find you.';
  if (current && tag === current) return 'This is your user tag.';
  const problem = tagProblem(tag);
  if (problem) return problem;
  if (checking) return 'Checking…';
  if (available === false) return reason || 'That user tag is taken.';
  if (available === true) return `@${tag} is free.`;
  return '';
}

/**
 * Where a signed-in member with no user tag is sent (PRIVACY_PLAN.md §15):
 * the step, with the route they wanted to return to. Null when they have a
 * tag, when they said "Not now" (tagSkipped, kept on the account so no device
 * asks again, 14 Oct 2026), when the setup screen or a REQUIRED form is owed (those walls go
 * first), on the step itself, and on the exits every wall leaves open.
 */
export function userTagWallDestination(fullPath, user, exits = []) {
  if (!user?.id || user.tag) return null;
  if (user.tagSkipped) return null;
  if (user.setupRequired) return null;
  const pending = user.pendingFormPrompt;
  if (pending?.id && (pending.mode === 'required' || pending.card?.mode === 'required')) return null;
  const path = String(fullPath || '').split('?')[0];
  if (path.startsWith('/welcome/')) return null;
  if (exits.some((e) => path.startsWith(e))) return null;
  return '/welcome/user-tag?redirect=' + encodeURIComponent(fullPath || '/');
}
